/**
 * BaseDashboardWidget
 * Återanvändbar widget-komponent för dashboard
 *
 * Ersätter: UnpluggedWidget, LowBatteryWidget, InspectionDueWidget
 *
 * Valfri `vehicleAssignments`: samma tilldelningsflöde som urkopplade (röd/gul rader,
 * markera ej tilldelade, mass-tilldela).
 */

import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { assignmentApi } from '@/services/assignments/assignmentApi';
import { ChevronDownIcon, ChevronUpIcon, ArrowPathIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { DashboardVehicleAssignModal } from './DashboardVehicleAssignModal';
import { usePermissions } from '@/hooks/usePermissions';
import { getWidgetFeatureForAssignmentCategory } from '@/constants/dashboardWidgets';

export type ColorScheme = 'red' | 'yellow' | 'orange' | 'blue' | 'green' | 'purple' | 'teal';

const colorConfig: Record<ColorScheme, {
  icon: string;
  badge: string;
  spinner: string;
  itemBg: string;
  itemText: string;
}> = {
  red: {
    icon: 'text-red-500',
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    spinner: 'border-red-500',
    itemBg: 'bg-red-50 dark:bg-red-900/20',
    itemText: 'text-red-700 dark:text-red-300',
  },
  yellow: {
    icon: 'text-yellow-500',
    badge: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    spinner: 'border-yellow-500',
    itemBg: 'hover:bg-bgHover',
    itemText: 'text-textDefault',
  },
  orange: {
    icon: 'text-orange-500',
    badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    spinner: 'border-orange-500',
    itemBg: 'hover:bg-bgHover',
    itemText: 'text-textDefault',
  },
  blue: {
    icon: 'text-blue-500',
    badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    spinner: 'border-blue-500',
    itemBg: 'hover:bg-bgHover',
    itemText: 'text-textDefault',
  },
  green: {
    icon: 'text-green-500',
    badge: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    spinner: 'border-green-500',
    itemBg: 'hover:bg-bgHover',
    itemText: 'text-textDefault',
  },
  purple: {
    icon: 'text-purple-500',
    badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    spinner: 'border-purple-500',
    itemBg: 'hover:bg-bgHover',
    itemText: 'text-textDefault',
  },
  teal: {
    icon: 'text-teal-500',
    badge: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    spinner: 'border-teal-500',
    itemBg: 'hover:bg-bgHover',
    itemText: 'text-textDefault',
  },
};

interface VehicleAssignmentRow {
  assignmentId: number;
  assignedToName: string;
  status: string;
}

export interface DashboardVehicleAssignmentOptions<T> {
  /** assignment_categories.code, t.ex. INSPECTION eller LOW_BATTERY */
  categoryCode: string;
  getVehicleId: (item: T) => number;
  getRegistrationNumber: (item: T) => string;
  buildAssignmentTitle: (item: T) => string;
}

export interface BaseDashboardWidgetProps<T> {
  icon: React.ComponentType<{ className?: string }>;
  colorScheme: ColorScheme;

  title: string;
  subtitle?: string;
  emptyMessage?: string;
  warningBadge?: string | ((count: number) => string);

  apiEndpoint: string;
  apiParams?: Record<string, string | number>;

  renderItem: (item: T, index: number) => ReactNode;
  getItemKey: (item: T, index: number) => string | number;

  showBadgeWhenEmpty?: boolean;
  autoRefreshMs?: number;
  onDataLoaded?: (data: T[], meta?: any) => void;

  renderBadge?: (data: T[], meta?: any) => ReactNode;

  getItemClassName?: (item: T, index: number) => string;

  footerAction?: ReactNode;

  /** Person-tilldelning per fordon (samma API som urkopplade) */
  vehicleAssignments?: DashboardVehicleAssignmentOptions<T>;
}

export function BaseDashboardWidget<T>({
  icon: Icon,
  colorScheme,
  title,
  subtitle,
  emptyMessage,
  warningBadge,
  apiEndpoint,
  apiParams = {},
  renderItem,
  getItemKey,
  showBadgeWhenEmpty = false,
  autoRefreshMs,
  onDataLoaded,
  renderBadge,
  getItemClassName,
  footerAction,
  vehicleAssignments,
}: BaseDashboardWidgetProps<T>) {
  const { t } = useTranslation();
  const { hasActionAccess, isUnlimited } = usePermissions();
  const [expanded, setExpanded] = useState(false);
  const [assignmentMap, setAssignmentMap] = useState<Record<number, VehicleAssignmentRow>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showAssignModal, setShowAssignModal] = useState(false);

  const colors = colorConfig[colorScheme];

  const onDataLoadedRef = React.useRef(onDataLoaded);
  onDataLoadedRef.current = onDataLoaded;

  const queryKey = ['dashboard-widget', apiEndpoint, JSON.stringify(apiParams)];

  const { data: responseData, isLoading: loading, error: queryError, refetch } = useQuery(
    queryKey,
    async () => {
      const response = await api.get(apiEndpoint, { params: apiParams });
      if (response.data.success) {
        return { data: response.data.data || [], meta: response.data.meta || null };
      }
      throw new Error(response.data.error || 'Failed to load data');
    },
    {
      staleTime: 60 * 1000,
      refetchInterval: autoRefreshMs || false,
      onSuccess: (result) => {
        onDataLoadedRef.current?.(result.data, result.meta);
      },
    }
  );

  const data: T[] = responseData?.data ?? [];
  const meta = responseData?.meta ?? null;
  const error = queryError ? (queryError as Error).message : null;

  const canLoadAssignments = useCallback(() => {
    if (!vehicleAssignments) return false;
    if (isUnlimited) return true;
    if (hasActionAccess('ASSIGNMENTS', 'VIEW')) return true;
    const widgetFeature = getWidgetFeatureForAssignmentCategory(vehicleAssignments.categoryCode);
    return widgetFeature ? hasActionAccess(widgetFeature, 'VIEW') : false;
  }, [vehicleAssignments, hasActionAccess, isUnlimited]);

  const loadAssignments = useCallback(async () => {
    if (!vehicleAssignments || !canLoadAssignments()) {
      setAssignmentMap({});
      return;
    }
    const ids = data.map(d => vehicleAssignments.getVehicleId(d));
    if (ids.length === 0) {
      setAssignmentMap({});
      return;
    }
    try {
      const map = await assignmentApi.getOpenByVehicleIds(ids, vehicleAssignments.categoryCode);
      setAssignmentMap(map);
    } catch {
      setAssignmentMap({});
    }
  }, [data, vehicleAssignments, canLoadAssignments]);

  useEffect(() => {
    if (!vehicleAssignments) {
      setAssignmentMap({});
      setSelected(new Set());
      return;
    }
    void loadAssignments();
  }, [vehicleAssignments, loadAssignments]);

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!vehicleAssignments) return;
    const unassignedIds = data
      .filter(d => !assignmentMap[vehicleAssignments.getVehicleId(d)])
      .map(d => vehicleAssignments.getVehicleId(d));
    if (unassignedIds.length > 0 && unassignedIds.every(id => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(unassignedIds));
    }
  };

  const unassignedCount = vehicleAssignments
    ? data.filter(d => !assignmentMap[vehicleAssignments.getVehicleId(d)]).length
    : 0;
  const assignedCount = vehicleAssignments ? data.length - unassignedCount : 0;

  const handleAssigned = () => {
    setShowAssignModal(false);
    setSelected(new Set());
    void refetch();
    void loadAssignments();
  };

  if (loading && data.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${colors.spinner}`} />
      </div>
    );
  }

  if (error && data.length === 0) {
    return <div className="text-red-500 text-sm py-2">{error}</div>;
  }

  const getBadgeContent = () => {
    if (renderBadge) {
      return renderBadge(data, meta);
    }

    if (!warningBadge) return null;
    if (!showBadgeWhenEmpty && data.length === 0) return null;

    const badgeText = typeof warningBadge === 'function'
      ? warningBadge(data.length)
      : warningBadge;

    return (
      <span className={`text-xs font-medium px-2 py-1 rounded-full ${colors.badge}`}>
        {badgeText}
      </span>
    );
  };

  const rowClassName = (item: T, index: number, vehicleId: number): string => {
    if (vehicleAssignments) {
      const isAssigned = !!assignmentMap[vehicleId];
      return isAssigned
        ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300';
    }
    if (getItemClassName) {
      return getItemClassName(item, index);
    }
    return `${colors.itemBg} ${colors.itemText}`;
  };

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-bgHover rounded-lg p-2 -m-2"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-8 h-8 ${colors.icon}`} />
          <div>
            <div className="text-2xl font-bold text-textDefault">{data.length}</div>
            <div className="text-xs text-textSecondary">{subtitle || title}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {getBadgeContent()}
          {vehicleAssignments && assignedCount > 0 && (
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
              {assignedCount} {t('assignments.assigned')}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void refetch();
              void loadAssignments();
            }}
            className="p-1 hover:bg-bgElevated rounded"
            title={t('common.refresh')}
          >
            <ArrowPathIcon className={`w-4 h-4 text-textSecondary ${loading ? 'animate-spin' : ''}`} />
          </button>
          {expanded ? (
            <ChevronUpIcon className="w-5 h-5 text-textSecondary" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-textSecondary" />
          )}
        </div>
      </div>

      {expanded && data.length > 0 && (
        <div className="mt-3 pt-3 border-t border-borderDefault">
          {vehicleAssignments && (
            <div className="flex items-center justify-between mb-2 px-1">
              <label className="flex items-center gap-2 text-xs text-textSecondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={unassignedCount > 0 && data
                    .filter(d => !assignmentMap[vehicleAssignments.getVehicleId(d)])
                    .every(d => selected.has(vehicleAssignments.getVehicleId(d)))}
                  onChange={toggleAll}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                {t('assignments.selectAll')} ({unassignedCount})
              </label>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowAssignModal(true); }}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  <UserPlusIcon className="w-3.5 h-3.5" />
                  {t('assignments.assignSelected')} ({selected.size})
                </button>
              )}
            </div>
          )}
          <div className="max-h-64 overflow-y-auto space-y-1">
            {data.map((item, index) => {
              const vehicleId = vehicleAssignments ? vehicleAssignments.getVehicleId(item) : 0;
              const assignment = vehicleAssignments ? assignmentMap[vehicleId] : undefined;
              const isAssigned = !!assignment;

              return (
                <div
                  key={getItemKey(item, index)}
                  className={`py-1.5 px-2 rounded text-sm flex items-start gap-2 ${rowClassName(item, index, vehicleId)}`}
                >
                  {vehicleAssignments && !isAssigned && (
                    <input
                      type="checkbox"
                      checked={selected.has(vehicleId)}
                      onChange={() => toggleSelect(vehicleId)}
                      className="mt-1 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                    />
                  )}
                  {vehicleAssignments && isAssigned && <div className="w-4 flex-shrink-0" />}

                  <div className="flex-1 min-w-0">
                    {renderItem(item, index)}
                    {vehicleAssignments && isAssigned && assignment && (
                      <div className="text-xs font-medium text-yellow-700 dark:text-yellow-300 mt-0.5">
                        → {assignment.assignedToName}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {expanded && data.length === 0 && emptyMessage && (
        <div className="mt-3 pt-3 border-t border-borderDefault text-center text-green-600 text-sm py-4">
          ✓ {emptyMessage}
        </div>
      )}

      {expanded && footerAction && (
        <div className="mt-2 pt-2 border-t border-borderDefault flex justify-center">
          {footerAction}
        </div>
      )}

      {showAssignModal && vehicleAssignments && (
        <DashboardVehicleAssignModal
          items={data
            .filter(d => selected.has(vehicleAssignments.getVehicleId(d)))
            .map(d => ({
              vehicleId: vehicleAssignments.getVehicleId(d),
              registrationNumber: vehicleAssignments.getRegistrationNumber(d),
              title: vehicleAssignments.buildAssignmentTitle(d),
            }))}
          defaultCategoryCode={vehicleAssignments.categoryCode}
          onClose={() => setShowAssignModal(false)}
          onAssigned={handleAssigned}
        />
      )}
    </div>
  );
}

export default BaseDashboardWidget;
